import { addDays, setHours, setMinutes, isSunday, startOfDay, format, isSaturday } from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime } from 'date-fns-tz';
import { settings } from '../config/settings';
import { isHoliday } from './businessHours';
import { FormController, formController as defaultFormController } from '../controllers/FormController';

const TIMEZONE = 'America/Los_Angeles';

export class BusinessHoursScheduler {
  private formController: FormController;
  private currentDate: Date;
  private cachedSlots: Map<string, { availableSlots: Date[]; allSlots: Date[]; cachedAt: number }> = new Map();
  private readonly SLOTS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes for slots cache (shorter than API cache)

  constructor(formController: FormController) {
    this.formController = formController;
    this.currentDate = new Date();
  }

  /**
   * Check if a cached slots result is still valid
   * @param cachedAt - Timestamp when slots were cached
   * @returns boolean - True if cache is still valid
   */
  private isSlotsCacheValid(cachedAt: number): boolean {
    return (Date.now() - cachedAt) < this.SLOTS_CACHE_TTL;
  }

  /**
   * Generate a cache key for slots based on current time and date range
   * @returns string - Cache key
   */
  private generateSlotsCacheKey(): string {
    const now = new Date();
    const hour = now.getHours();
    const minute = Math.floor(now.getMinutes() / 15) * 15; // Round to nearest 15-minute interval
    return `${format(now, 'yyyy-MM-dd')}-${hour}:${minute}`;
  }

  /**
   * Check if current time is within business hours
   * @returns boolean - True if within business hours
   */
  isBusinessHours(): boolean {
    const now = new Date();
    const pstNow = utcToZonedTime(now, TIMEZONE);
    
    // Check if it's a holiday
    if (isHoliday(pstNow)) return false;
    
    const todayStart = startOfDay(pstNow);
    let intervalStart: Date;
    let intervalEnd: Date;

    if (isSaturday(pstNow)) {
      // Saturday hours from settings
      intervalStart = setMinutes(setHours(todayStart, settings.businessHours.saturday.start), 0);
      intervalEnd = setMinutes(setHours(todayStart, settings.businessHours.saturday.end), 0);
    } else if (isSunday(pstNow)) {
      // Sunday hours from settings
      intervalStart = setMinutes(setHours(todayStart, settings.businessHours.sunday.start), 0);
      intervalEnd = setMinutes(setHours(todayStart, settings.businessHours.sunday.end), 0);
    } else {
      // Use weekday hours for all business days (including Saturday)
      intervalStart = setMinutes(setHours(todayStart, settings.businessHours.weekday.start), 0);
      intervalEnd = setMinutes(setHours(todayStart, settings.businessHours.weekday.end), 0);
    }
    
    return pstNow >= intervalStart && pstNow < intervalEnd;
  }

  /**
   * Get available time slots using real availability data
   * @param useCache - Whether to use cached slots (default: true)
   * @returns Promise<{ availableSlots: Date[]; allSlots: Date[] }> - Available and all slots
   */
  async getAvailableTimeSlots(useCache: boolean = true): Promise<{ availableSlots: Date[]; allSlots: Date[] }> {
    const cacheKey = this.generateSlotsCacheKey();
    
    // Check cache first
    if (useCache && this.cachedSlots.has(cacheKey)) {
      const cached = this.cachedSlots.get(cacheKey)!;
      if (this.isSlotsCacheValid(cached.cachedAt)) {
        return { availableSlots: cached.availableSlots, allSlots: cached.allSlots };
      }
    }

    const availableSlots: Date[] = [];
    const allSlots: Date[] = [];
    const now = new Date();
    const pstNow = utcToZonedTime(now, TIMEZONE);
    let currentDate = startOfDay(pstNow);
    let businessDaysFound = 0;
    
    // Look ahead up to 5 days to find 3 available business days
    for (let i = 0; i < 5 && businessDaysFound < 3; i++) {
      // Skip weekends (only Sunday) and holidays
      if (!isSunday(currentDate) && 
          !isHoliday(currentDate)) {
        
        const dateString = format(currentDate, 'yyyy-MM-dd');
        const isToday = currentDate.getDate() === pstNow.getDate();
        
        try {
          // Get real availability data for this date
          const availabilityData = await this.formController.getDateAvailability(dateString);
          
          const dayAllSlots: Date[] = [];
          const dayAvailableSlots: Date[] = [];

          if (availabilityData.success) {
            // Use real availability data
            const dayStart = isToday ? pstNow : setMinutes(setHours(currentDate, isSaturday(currentDate) ? settings.businessHours.saturday.start : settings.businessHours.weekday.start), 0);
            const dayEnd = setMinutes(setHours(currentDate, isSaturday(currentDate) ? settings.businessHours.saturday.end : settings.businessHours.weekday.end), 0);
            
            // Generate slots for the day
            let currentSlot = dayStart;
            if (isToday) {
              // Round up to next 15-minute interval and then add a 15-minute buffer
              const startMinutes = dayStart.getMinutes();
              const roundedMinutes = Math.ceil(startMinutes / 15) * 15;
              currentSlot = setMinutes(dayStart, roundedMinutes);
              currentSlot = new Date(currentSlot.getTime() + 15 * 60 * 1000);
            }
            
            while (currentSlot < dayEnd) {
              // Convert PST slot to UTC for storage
              const utcSlot = zonedTimeToUtc(currentSlot, TIMEZONE);
              dayAllSlots.push(utcSlot);
              
              // Format time slot for comparison with availability data
              const timeSlot = format(currentSlot, 'HH:mm');
              
              // Check if this timeslot is available according to real data
              const isAvailable = availabilityData.timeslots.some(slot => 
                slot.timeSlot === timeSlot && slot.isAvailable
              );
              
              if (isAvailable) {
                dayAvailableSlots.push(utcSlot);
              }
              
              // Add 15 minutes
              currentSlot = new Date(currentSlot.getTime() + 15 * 60 * 1000);
            }
          } else {
            // Fallback to limited availability if API call fails
            console.warn(`Failed to get availability for ${dateString}:`, availabilityData.error);
            
            // Generate minimal slots as fallback
            const dayStart = isToday ? pstNow : setMinutes(setHours(currentDate, isSaturday(currentDate) ? settings.businessHours.saturday.start : settings.businessHours.weekday.start), 0);
            const dayEnd = setMinutes(setHours(currentDate, isSaturday(currentDate) ? settings.businessHours.saturday.end : settings.businessHours.weekday.end), 0);
            
            let currentSlot = dayStart;
            if (isToday) {
              // Round up to next 15-minute interval and then add a 15-minute buffer
              const startMinutes = dayStart.getMinutes();
              const roundedMinutes = Math.ceil(startMinutes / 15) * 15;
              currentSlot = setMinutes(dayStart, roundedMinutes);
              currentSlot = new Date(currentSlot.getTime() + 15 * 60 * 1000);
            }
            
            while (currentSlot < dayEnd) {
              const utcSlot = zonedTimeToUtc(currentSlot, TIMEZONE);
              dayAllSlots.push(utcSlot);
              
              // Only show limited slots as fallback (10 AM and 2 PM)
              if (currentSlot.getHours() === 10 || currentSlot.getHours() === 14) {
                dayAvailableSlots.push(utcSlot);
              }
              
              currentSlot = new Date(currentSlot.getTime() + 15 * 60 * 1000);
            }
          }

          if (dayAvailableSlots.length > 0) {
            allSlots.push(...dayAllSlots);
            availableSlots.push(...dayAvailableSlots);
            businessDaysFound++;
          }
        } catch (error) {
          console.error(`Error getting availability for ${dateString}:`, error);
          // Continue to next day on error
        }
      }
      
      currentDate = addDays(currentDate, 1);
    }
    
    const result = { availableSlots, allSlots };
    
    // Cache the result
    this.cachedSlots.set(cacheKey, {
      ...result,
      cachedAt: Date.now()
    });
    
    // Clean up old cache entries
    this.cleanupSlotsCache();
    
    return result;
  }

  /**
   * Check if a specific date and time is available
   * @param date - Date object or string in YYYY-MM-DD format
   * @param timeSlot - Time slot in HH:MM format (e.g., "09:00")
   * @returns Promise<boolean> - True if available
   */
  async isTimeslotAvailable(date: string | Date, timeSlot: string): Promise<boolean> {
    return await this.formController.isTimeslotAvailable(date, timeSlot);
  }

  /**
   * Get all available timeslots for a specific date
   * @param date - Date object or string in YYYY-MM-DD format
   * @returns Promise<string[]> - Array of available timeslots in HH:MM format
   */
  async getAvailableTimeslots(date: string | Date): Promise<string[]> {
    return await this.formController.getAvailableTimeslots(date);
  }

  /**
   * Get the next available appointment slot
   * @param startDate - Date to start looking from (default: now)
   * @returns Promise<Date | null> - Next available slot or null if none found
   */
  async getNextAvailableSlot(startDate: Date = new Date()): Promise<Date | null> {
    const { availableSlots } = await this.getAvailableTimeSlots();
    
    const futureSlots = availableSlots.filter(slot => slot > startDate);
    
    if (futureSlots.length === 0) {
      return null;
    }
    
    // Return the earliest available slot
    return futureSlots.sort((a, b) => a.getTime() - b.getTime())[0];
  }

  /**
   * Get availability summary for multiple dates
   * @param days - Number of days to look ahead (default: 5)
   * @returns Promise<Array<{ date: string; availableSlots: number; totalSlots: number; percentage: number }>>
   */
  async getAvailabilitySummary(days: number = 5): Promise<Array<{
    date: string;
    availableSlots: number;
    totalSlots: number;
    percentage: number;
  }>> {
    const summary = [];
    const startDate = new Date();
    
    for (let i = 0; i < days; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      const dateString = format(date, 'yyyy-MM-dd');
      
      try {
        const availability = await this.formController.getDateAvailability(dateString);
        
        if (availability.success) {
          const totalSlots = availability.timeslots.length;
          const availableSlots = availability.timeslots.filter(slot => slot.isAvailable).length;
          const percentage = totalSlots > 0 ? Math.round((availableSlots / totalSlots) * 100) : 0;
          
          summary.push({
            date: dateString,
            availableSlots,
            totalSlots,
            percentage
          });
        } else {
          summary.push({
            date: dateString,
            availableSlots: 0,
            totalSlots: 0,
            percentage: 0
          });
        }
      } catch (error) {
        console.error(`Error getting availability for ${dateString}:`, error);
        summary.push({
          date: dateString,
          availableSlots: 0,
          totalSlots: 0,
          percentage: 0
        });
      }
    }
    
    return summary;
  }

  /**
   * Clean up old cache entries
   */
  private cleanupSlotsCache(): void {
    const now = Date.now();
    
    for (const [key, cache] of this.cachedSlots.entries()) {
      if (now - cache.cachedAt > this.SLOTS_CACHE_TTL) {
        this.cachedSlots.delete(key);
      }
    }
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.cachedSlots.clear();
    this.formController.clearAvailabilityCache();
  }

  /**
   * Get cache statistics (useful for debugging)
   */
  getCacheStats(): { slotsCache: number; apiCache: number } {
    return {
      slotsCache: this.cachedSlots.size,
      apiCache: Object.keys((this.formController as any).availabilityCache).length
    };
  }
}

// Export singleton instance for backward compatibility
export const businessHoursScheduler = new BusinessHoursScheduler(defaultFormController);

// Export function for backward compatibility
export async function getAvailableTimeSlots(): Promise<{ availableSlots: Date[]; allSlots: Date[] }> {
  return await businessHoursScheduler.getAvailableTimeSlots();
} 